import { Injectable, inject, computed } from '@angular/core';
import { IUser, User } from '@app/store/user';
import { Store } from '@ngrx/store';
import { State } from '@app/store';
import { getUser } from '@app/store/user/user.reducer';
import { toSignal } from '@angular/core/rxjs-interop';

export enum PrivilegeType {
  SUPER_ADMIN = 'WEB_SUADMIN',
  CATEGORY_EDIT_ALL = 'WEB_ADMIN_CATEGORYMGNT_EDIT_ALL',
  CATEGORY_VIEW_ALL = 'WEB_ADMIN_CATEGORYMGNT_VIEW_ALL',
  CATEGORY_EDIT_PREFIX = 'WEB_ADMIN_CATEGORYMGNT_EDIT_',
  CATEGORY_VIEW_PREFIX = 'WEB_ADMIN_CATEGORYMGNT_VIEW_',
  CATEGORY_MGMT = 'WEB_ADMIN_CATEGORYMGNT',
  ADMIN_PRIVILEGES = 'WEB_ADMIN_PRIVILEGES',
  ADMIN_FILLOUT_REPORT = 'FLAG_WEB_ADMIN_FILLOUTREPORT',
  REPORT_HISTORY_MANAGEMENT_ACCESS = 'WEB_HISTORY_MANAGEMENT_ACCESS'
}

export interface ICategoryAdminAccess {
  view: boolean;
  edit: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class PrivilegesService {
  private readonly store = inject(Store<State>);

  // Convert the user stream to a signal
  private readonly currentUser = toSignal(this.store.select(getUser), { initialValue: null });

  // Computed signals for privilege checks
  readonly isCurrentUserSuperAdmin = computed(() => {
    const user = this.currentUser();
    return user ? this.isSuperAdminUser(user) : false;
  });

  readonly isCurrentUserAdmin = computed(() => {
    const user = this.currentUser();
    return user ? this.isAdminUser(user) : false;
  });

  readonly isCurrentUserFillOutReportAdmin = computed(() => {
    const user = this.currentUser();
    return user ? this.isFillOutReportAdminUser(user) : false;
  });

  readonly isCurrentUserCategoryUser = computed(() => {
    const user = this.currentUser();
    return user ? this.isCategoryUser(user) : false;
  });

  readonly isCurrentUserTagUser = computed(() => {
    const user = this.currentUser();
    return user ? this.isTagUser(user) : false;
  });

  readonly isCurrentUserPrivilegeUser = computed(() => {
    const user = this.currentUser();
    return user ? this.isPrivilegeUser(user) : false;
  });

  readonly isCurrentUserReportHistoryManager = computed(() => {
    const user = this.currentUser();
    return user ? this.isReportHistoryManager(user) : false;
  });

  // Synchronous privilege check methods - when you have a user object
  isSuperAdminUser(user: IUser): boolean {
    return User.hasPrivilege(user, PrivilegeType.SUPER_ADMIN);
  }

  isAdminUser(user: IUser): boolean {
    return this.isSuperAdminUser(user) || this.isCategoryUser(user) || this.isTagUser(user) || this.isPrivilegeUser(user);
  }

  isFillOutReportAdminUser(user: IUser): boolean {
    return User.hasPrivilege(user, PrivilegeType.ADMIN_FILLOUT_REPORT);
  }

  isCategoryUser(user: IUser): boolean {
    return User.hasPrivilege(user, PrivilegeType.SUPER_ADMIN) || User.hasPrivilegeSubStr(user, PrivilegeType.CATEGORY_MGMT);
  }

  isCategoryUserByProfileId(user: IUser, profileId: number): ICategoryAdminAccess {
    const access: ICategoryAdminAccess = {
      view: false,
      edit: false
    };

    access.view = User.hasPrivilegeArray(
      user,
      [
        PrivilegeType.SUPER_ADMIN,
        PrivilegeType.CATEGORY_VIEW_ALL,
        PrivilegeType.CATEGORY_VIEW_PREFIX + profileId
      ]);

    access.edit = User.hasPrivilegeArray(
      user,
      [
        PrivilegeType.SUPER_ADMIN,
        PrivilegeType.CATEGORY_EDIT_ALL,
        PrivilegeType.CATEGORY_EDIT_PREFIX + profileId
      ]);

    return access;
  }

  isTagUser(user: IUser): boolean {
    return User.hasPrivilege(user, PrivilegeType.SUPER_ADMIN);
  }

  isPrivilegeUser(user: IUser): boolean {
    return User.hasPrivilege(user, PrivilegeType.SUPER_ADMIN) || User.hasPrivilegeSubStr(user, PrivilegeType.ADMIN_PRIVILEGES);
  }

  isReportHistoryManager(user: IUser): boolean {
    return User.hasPrivilege(user, PrivilegeType.REPORT_HISTORY_MANAGEMENT_ACCESS);
  }

  // Method to get category access by profile ID as a signal
  isCurrentUserCategoryUserByProfileId(profileId: number) {
    return computed(() => {
      const user = this.currentUser();
      return user && profileId >= 0 ? this.isCategoryUserByProfileId(user, profileId) : { view: false, edit: false };
    });
  }
}
